import { Button, Form, Input, notification, Select } from "antd";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { clearError } from "store/slices/authSlice";
import { LoginPayload } from "types/authTypes";
import type { InputRef } from 'antd';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line 
const { Option } = Select;

interface LoginFormProps {
    onFinish: (values: LoginPayload) => void;
    error: string | null;
    setIsForgotPassword: (isForgotPassword: boolean) => void;
    setIsAlternateLogin: (isAlternateLogin: boolean) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onFinish, error, setIsForgotPassword, setIsAlternateLogin }) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const emailInputRef = useRef<InputRef>(null);
    const { t, i18n } = useTranslation();

    const supportedLangs = ['en', 'es', 'de', 'it', 'fr', 'pt'];
    const currentLang = supportedLangs.includes(i18n.language)
        ? i18n.language
        : (supportedLangs.includes(i18n.language.split('-')[0])
            ? i18n.language.split('-')[0]
            : 'en');

    useEffect(() => {
        if (emailInputRef.current) {
            emailInputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (error) {
            notification.error({
                message: t('notification.error'),
                description: t('auth.loginFailed'),
                placement: 'bottomRight',
            });
            dispatch(clearError());
        }
    }, [dispatch, error, t]);

    // eslint-disable-next-line 
    const handleLanguageChange = (lang: string) => {
        i18n.changeLanguage(lang);
        localStorage.setItem('i18nextLng', lang);
    };

    return (
        <>
            <Form
                form={form}
                className="login-form"
                layout="vertical"
                onFinish={onFinish}
                validateTrigger="onSubmit"

            >
                <Form.Item
                    name="email"
                    label={t('auth.email')}
                    normalize={(value) => value?.trim()}
                    rules={[
                        { required: true, message: t('auth.emailRequired') },
                        { type: 'email', message: t('auth.emailInvalid') },
                    ]}
                    style={{ marginBottom: '24px' }}
                >
                    <Input autoComplete="off" ref={emailInputRef} />
                </Form.Item>


                <Form.Item
                    name="password"
                    label={t('auth.password')}
                    validateTrigger="onSubmit"
                    normalize={(value) => value?.trim()}
                    rules={[{ required: true, message: t('auth.passwordRequired') }]}
                >
                    <Input.Password autoComplete="off" />
                </Form.Item>

                <Form.Item style={{ marginBottom: '6px' }}>
                    <Button type="primary" htmlType="submit" className="button">
                        {t('auth.signIn')}
                    </Button>
                </Form.Item>
            </Form>

            <div>
                <Button
                    onClick={() => setIsForgotPassword(true)}
                    type="link"
                    className="button"
                    style={{ fontWeight: 'bold' }}
                >
                    {t('auth.forgotPassword')}
                </Button>
                <Button
                    onClick={() => setIsAlternateLogin(true)}
                    className="button"
                    style={{ marginTop: '30px' }}
                >
                    {t('auth.alternativeLogin')}
                </Button>
            </div>

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                <Select
                    defaultValue={currentLang}
                    onChange={handleLanguageChange}
                    style={{ width: 120 }}
                >
                    <Option value="en">English</Option>
                    <Option value="es">Español</Option>
                    {/* <Option value="ko">한국어</Option> */}
                    <Option value="de">Deutsch</Option>
                    <Option value="it">Italiano</Option>
                    <Option value="fr">Français</Option>
                    <Option value="pt">Português</Option>
                </Select>
            </div>

        </>
    );
};

export default LoginForm;
